import * as signalR from '@microsoft/signalr';
import { useEffect, useState } from 'react';
import { getOidcApiRoot, getOidcToken } from '../env-vars';
import AuthService from '../services/AuthService';

type SignalREvent =
  | 'adddealuserscompleted'
  | 'orderfulfilled'
  | 'newagreementadded';

const authService = new AuthService();

class SignalRConnector {
  private connection: signalR.HubConnection;

  private started: boolean = false;

  private failed = false;

  private failedTries = 0;

  static instance: SignalRConnector;

  static MAX_FAILED_TRIES = 5;

  constructor() {
    const token = localStorage.getItem(getOidcToken());
    const currentTimeUtc = Math.floor(new Date().getTime() / 1000);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${getOidcApiRoot()}/api/signalr/notifications`, {
        accessTokenFactory: async () => {
          const user = await authService.getUser();
          return user?.access_token;
        },
        skipNegotiation:
          !token || currentTimeUtc >= JSON.parse(token).expires_at,
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();
  }

  public on(event: SignalREvent, callback: (args: any) => void) {
    this.connection.on(event, callback);
  }

  public off(event: SignalREvent, callback: (args: any) => void) {
    this.connection.off(event, callback);
  }

  public async start() {
    if (this.started || this.failed) return;
    try {
      await this.connection.start();
      this.started = true;
      this.failed = false;
      this.failedTries = 0;
      console.log('SignalR Connected.');
    } catch (err) {
      this.failedTries += 1;
      if (this.failedTries === SignalRConnector.MAX_FAILED_TRIES)
        this.failed = true;
      console.log(err);
      const _this = this;
      setTimeout(() => {
        _this.start();
      }, 1000);
    }
  }

  public static getInstance(): SignalRConnector {
    if (!SignalRConnector.instance)
      SignalRConnector.instance = new SignalRConnector();
    return SignalRConnector.instance;
  }
}

export default function useSignalREventConnector(
  event: SignalREvent,
  handler?: (data: any) => void,
) {
  useEffect(() => {
    const connector = SignalRConnector.getInstance();
    connector.on(event, handler);

    return () => {
      connector.off(event, handler);
    };
  }, []);
}

export function useSignalRStartAction(skip = false) {
  const [userToken, setUserToken] = useState<string>();

  authService.getUser().then((user) => {
    if (user?.access_token && !userToken) setUserToken(user.access_token);
  });

  useEffect(() => {
    if (userToken && !skip) {
      const connector = SignalRConnector.getInstance();
      connector.start().then();
    }
  }, [userToken, skip]);
}
